/**
 * GoogleAccessPrompt — the one question asked when a feature needs more of
 * Google than the person granted so far: "Allow CorpBrain to read your Drive
 * files so the agent can search them" (or to edit them), Allow / Not now.
 *
 * Allow opens Google's incremental consent in a popup; the page behind it —
 * the open picker, the running chat turn — stays where it was, and
 * `onGranted` carries on with what the person was doing.
 *
 * Two shapes: `panel` fills a dialog's body (the Drive picker, the folder
 * choosers); `card` sits in the chat while an agent's turn waits for it.
 */

import { useEffect, useRef, useState } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { GoogleDriveGlyph } from '@/modules/conversations/components/drive/driveIcons';
import {
  declineGoogleAccess,
  requestGoogleAccess,
  type AccessRequest,
  type GoogleAccessLevel,
  type GoogleAccessOutcome,
} from '../services/googleAccessService';
import '@/modules/conversations/components/drive/DriveFilePicker.css';
import './GoogleAccessPrompt.css';

export type GoogleAccessReason = 'pick' | 'browse' | 'folders' | 'agentRead' | 'agentEdit';

interface GoogleAccessPromptProps {
  access: Exclude<GoogleAccessLevel, 'basic'>;
  reason: GoogleAccessReason;
  variant?: 'panel' | 'card';
  /** Optional: in a chat the waiting tool carries on by itself. */
  onGranted?: () => void;
  /** "Not now", or Google's own Cancel. Omit to hide the Not now button. */
  onDeclined?: () => void;
  /** Tell a waiting agent tool about "Not now" (chat only). */
  reportDecline?: boolean;
}

type Phase = 'idle' | 'waiting' | GoogleAccessOutcome;

export function GoogleAccessPrompt({
  access, reason, variant = 'panel', onGranted, onDeclined, reportDecline = false,
}: GoogleAccessPromptProps) {
  const { t } = useLanguage();
  const [phase, setPhase] = useState<Phase>('idle');
  const request = useRef<AccessRequest | null>(null);
  const answered = useRef(false);
  const reportOnClose = useRef(reportDecline);
  reportOnClose.current = reportDecline;

  // Closing the prompt without an answer (the person navigated away, the
  // card was dismissed) must not leave an agent's tool waiting out its whole
  // timeout: it counts as "Not now". After a grant this is never sent.
  // Not while Google's window is open: then the card was removed because the
  // agent's wait ended (granted elsewhere, Stop), and a "Not now" stamped now
  // could end another wait that just began.
  // Deferred, and called off if the card mounts again: React may unmount and
  // remount a component in place (StrictMode does it to every one in
  // development), and that is not the person closing anything — seen live,
  // the card declined itself 120 ms after it appeared.
  const closeTimer = useRef<number | undefined>(undefined);
  useEffect(() => {
    window.clearTimeout(closeTimer.current);
    return () => {
      const wasWaiting = request.current !== null;
      closeTimer.current = window.setTimeout(() => {
        request.current?.cancel();
        if (reportOnClose.current && !answered.current && !wasWaiting) void declineGoogleAccess();
      }, 0);
    };
  }, []);

  const allow = () => {
    // Synchronously, inside the click: a popup opened after an await is blocked.
    const req = requestGoogleAccess(access);
    request.current = req;
    setPhase('waiting');
    void req.outcome.then((outcome) => {
      request.current = null;
      if (outcome === 'granted') { answered.current = true; setPhase('granted'); onGranted?.(); return; }
      setPhase(outcome);
      if (outcome === 'declined' && reportDecline) { answered.current = true; void declineGoogleAccess(); }
    });
  };

  const notNow = () => {
    request.current?.cancel();
    answered.current = true;
    if (reportDecline) void declineGoogleAccess();
    onDeclined?.();
  };

  const note = phase === 'waiting' ? t('cloudStorage.access.waiting')
    : phase === 'blocked' ? t('cloudStorage.access.popupBlocked')
    : phase === 'declined' ? t('cloudStorage.access.declined')
    : phase === 'wrong_account' ? t('cloudStorage.access.wrongAccount')
    : phase === 'error' ? t('cloudStorage.access.failed')
    : phase === 'granted' ? t('cloudStorage.access.granted')
    : null;

  return (
    <div
      className={`google-access google-access--${variant}`}
      role={variant === 'card' ? 'group' : undefined}
      aria-label={t(`cloudStorage.access.${access}Title`)}
      aria-live="polite"
    >
      <span className="google-access__logo" aria-hidden="true"><GoogleDriveGlyph size={variant === 'card' ? 22 : 40} /></span>
      <div className="google-access__text">
        <h3 className="google-access__title">{t(`cloudStorage.access.${access}Title`)}</h3>
        <p className="google-access__body">{t(`cloudStorage.access.reason.${reason}`)}</p>
        {note && (
          <p className={`google-access__note ${phase === 'waiting' || phase === 'granted' ? '' : 'is-problem'}`}>{note}</p>
        )}
      </div>
      <div className="google-access__actions">
        {phase === 'waiting' ? (
          <button type="button" className="drive-picker__btn" onClick={() => { request.current?.cancel(); }}>
            {t('common.cancel')}
          </button>
        ) : phase !== 'granted' && (
          <>
            {onDeclined && (
              <button type="button" className="drive-picker__btn" onClick={notNow}>
                {t('cloudStorage.access.notNow')}
              </button>
            )}
            <button type="button" className="drive-picker__btn drive-picker__btn--primary" onClick={allow}>
              {phase === 'idle' ? t('cloudStorage.access.allow') : t('common.retry')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
